import { shouldOmitParentheses } from "@/components/expression/utils/functions";
import { groupsJson } from "@/components/suggestions/providers/data";
import { normalizeSuggestionKeywords } from "@/components/suggestions/providers/utils/normalizeKeywords";
import { selectedFunctionNode } from "@/input/utils/openGroup";

import { groupSuggestions } from "../utils";

import type { GroupTypeValue } from "@/components/suggestions/providers/data/types";
import type { SingleSuggestion, SuggestionProvider } from "@/components/suggestions/types";
import type { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import type { SuggestionIntlName } from "@/models/nodes/types";
import type { ParenthesesVisibility } from "@corca/data";

const BRACKET_STYLES: GroupTypeValue[] = ["round", "square", "curly", "angle"];

const VISIBILITY_INTL_NAME = {
  auto: "functionBrackets.auto",
  visible: "functionBrackets.enable",
  hidden: "functionBrackets.disable",
} as const satisfies Record<ParenthesesVisibility, string>;

function resolvedStateIntlName(node: FunctionNodeModel): SuggestionIntlName {
  return shouldOmitParentheses(node) ? "functionBrackets.disabled" : "functionBrackets.enabled";
}

function visibilitySuggestion(
  node: FunctionNodeModel,
  visibility: ParenthesesVisibility,
  priority: number,
): SingleSuggestion<"group"> {
  return {
    type: "single",
    item: {
      type: "group",
      intlName: VISIBILITY_INTL_NAME[visibility],
      symbol: "",
    },
    action: { type: "set-function-parentheses-visibility", visibility },
    keywords: normalizeSuggestionKeywords({ keywords: ["brackets", "parens", "parentheses"] }),
    checked: node.parenthesesVisibility === visibility,
    categoryIntlName:
      visibility === "auto" && node.parenthesesVisibility === "auto"
        ? resolvedStateIntlName(node)
        : undefined,
    hideIcon: true,
    popupGroup: "bracketsVisibility",
    priority,
  };
}

function styleSuggestion(
  node: FunctionNodeModel,
  style: GroupTypeValue,
  priority: number,
): SingleSuggestion<"group"> {
  const group = groupsJson[style];

  return {
    type: "single",
    item: {
      type: "group",
      intlName: group.intlName,
      symbol: "",
      props: {
        openingSymbol: group.openingSymbol,
        closingSymbol: group.closingSymbol,
      },
    },
    action: { type: "set-function-group-symbols" },
    keywords: normalizeSuggestionKeywords({ keywords: ["brackets", "parens"] }),
    icon: group.icon,
    forceIcon: true,
    checked:
      node.openingSymbol === group.openingSymbol.symbol &&
      node.closingSymbol === group.closingSymbol.symbol,
    popupGroup: "bracketsStyle",
    priority,
  };
}

export const functionBracketsSuggestionsProvider: SuggestionProvider = {
  type: "functionBrackets",
  search: (_str, doc) => {
    const node = selectedFunctionNode(doc);

    if (!node) {
      return [];
    }

    const children: SingleSuggestion<"group">[] = [
      visibilitySuggestion(node, "auto", 1),
      visibilitySuggestion(node, "visible", 0.9),
      visibilitySuggestion(node, "hidden", 0.8),
      ...BRACKET_STYLES.map((style, index) => styleSuggestion(node, style, 0.7 - index * 0.05)),
    ];

    const parent: SingleSuggestion<"group"> = {
      type: "single",
      item: {
        type: "group",
        intlName: "functionBrackets.title",
        symbol: "",
      },
      icon: groupsJson.round.icon,
      forceIcon: true,
      indicatorIntlName:
        node.parenthesesVisibility === "auto"
          ? "functionBrackets.auto"
          : resolvedStateIntlName(node),
      popupGroup: "functionBrackets",
      priority: 1,
      nestedKey: "functionBrackets",
      nestedItems: groupSuggestions("", children),
    };

    return [[parent]];
  },
};
